/**
 * UIBugQueuePage — super-admin view of user-filed bug reports.
 * Route: /admin/ui-bug-queue
 * v3.3 (2026-07-01)
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Bug, Loader2, RefreshCw, Sparkles, Trash2, CheckCircle2, XCircle,
  Copy, Zap, ExternalLink, Camera, Terminal,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  pending: 'border-amber-300 text-amber-700 bg-amber-50',
  analyzed: 'border-cyan-300 text-cyan-700 bg-cyan-50',
  dismissed: 'border-zinc-300 text-zinc-600 bg-zinc-50',
  fixed: 'border-emerald-300 text-emerald-700 bg-emerald-50',
};

function AnalysisView({ analysis }) {
  if (!analysis) return null;
  return (
    <div className="mt-2 space-y-2 text-xs border border-gray-200 rounded-md p-2 bg-gray-50" data-testid="analysis-view">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-gray-800 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-cyan-500" /> AI Analysis
        </div>
        <Badge variant="outline" className="text-[10px] border-cyan-200 text-cyan-700">
          {analysis._model_used || 'kaharian'} · {Math.round((analysis.confidence || 0) * 100)}%
        </Badge>
      </div>
      {analysis.observations?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Observations</div>
          <ul className="list-disc list-inside space-y-0.5 text-gray-700">
            {analysis.observations.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </div>
      )}
      {analysis.detected_issues?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Issues</div>
          <div className="space-y-1">
            {analysis.detected_issues.map((iss, i) => (
              <div key={i} className="flex items-start gap-2">
                <Badge variant="outline" className={
                  'text-[9px] shrink-0 mt-0.5 ' +
                  (iss.severity === 'high' ? 'border-rose-400 text-rose-700 bg-rose-50' :
                   iss.severity === 'medium' ? 'border-amber-400 text-amber-700 bg-amber-50' :
                   'border-gray-300 text-gray-600 bg-gray-50')
                }>{iss.severity}</Badge>
                <div>
                  <div className="text-gray-800">{iss.issue}</div>
                  {iss.location_hint && <div className="text-[10px] text-gray-500">{iss.location_hint}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {analysis.suggested_changes?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Suggested changes</div>
          <div className="space-y-1">
            {analysis.suggested_changes.sort((a, b) => (a.priority || 99) - (b.priority || 99)).map((s, i) => (
              <div key={i} className="border border-gray-200 rounded p-2 bg-white">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <code className="text-[10px] text-cyan-700">{s.file_hint || 'unknown'}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`In ${s.file_hint || 'the codebase'}: ${s.description}`);
                      toast.success('Copied — paste into VibeKonnect chat.');
                    }}
                    className="text-[9px] text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5"
                    data-testid={`copy-suggestion-${i}`}
                  >
                    <Copy className="h-2.5 w-2.5" /> Copy as prompt
                  </button>
                </div>
                <div className="text-gray-800">{s.description}</div>
                <div className="text-[10px] text-gray-500 mt-1">{s.change_type} · priority {s.priority || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report, onAnalyze, onStatus, onDelete, onOpen }) {
  const [analyzing, setAnalyzing] = useState(false);

  const analyze = async () => {
    setAnalyzing(true);
    try { await onAnalyze(report.id); } finally { setAnalyzing(false); }
  };

  return (
    <Card className="border-gray-200" data-testid={`bug-report-${report.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gray-500 truncate">
              <a
                href={report.url}
                target="_blank"
                rel="noreferrer"
                className="text-cyan-700 hover:underline inline-flex items-center gap-0.5"
              >
                {(() => { try { return new URL(report.url).pathname; } catch { return report.url; } })()}
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {report.reporter_email} · <span className="font-mono">{report.reporter_role}</span> · {new Date(report.created_at).toLocaleString()}
            </div>
          </div>
          <Badge variant="outline" className={'text-[10px] shrink-0 ' + (STATUS_BADGES[report.status] || '')}>
            {report.status}
          </Badge>
        </div>

        {report.description && (
          <div className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">
            {report.description}
          </div>
        )}

        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onOpen(report.id)} className="h-7 text-[11px]" data-testid={`view-${report.id}`}>
            <Camera className="h-3 w-3 mr-1" /> View screenshot
          </Button>
          {report.status === 'pending' && (
            <Button
              size="sm"
              onClick={analyze}
              disabled={analyzing}
              className="h-7 text-[11px] bg-cyan-600 hover:bg-cyan-500"
              data-testid={`analyze-${report.id}`}
            >
              {analyzing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
              Analyze
            </Button>
          )}
          {report.status !== 'fixed' && (
            <Button size="sm" variant="outline" onClick={() => onStatus(report.id, 'fixed')} className="h-7 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`mark-fixed-${report.id}`}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> Mark fixed
            </Button>
          )}
          {report.status !== 'dismissed' && (
            <Button size="sm" variant="outline" onClick={() => onStatus(report.id, 'dismissed')} className="h-7 text-[11px] text-zinc-600 hover:bg-zinc-50" data-testid={`dismiss-${report.id}`}>
              <XCircle className="h-3 w-3 mr-1" /> Dismiss
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(report.id)} className="h-7 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-${report.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>

        {report.analysis && <AnalysisView analysis={report.analysis} />}
        {report.console_snippet && (
          <details className="text-[10px]">
            <summary className="cursor-pointer text-gray-500 flex items-center gap-1"><Terminal className="h-2.5 w-2.5" /> Console tail</summary>
            <pre className="mt-1 p-2 bg-zinc-950 text-zinc-200 rounded font-mono text-[10px] overflow-x-auto whitespace-pre-wrap">{report.console_snippet}</pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

export default function UIBugQueuePage() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState('pending');
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [screenshotOpen, setScreenshotOpen] = useState(null); // { url, media_type }

  const fetchAll = useCallback(async () => {
    setBusy(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/api/ui-bugs/queue`, { params: { status, limit: 100 } }),
        axios.get(`${API}/api/ui-bugs/stats`),
      ]);
      setReports(q.data.reports || []);
      setStats(s.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setBusy(false); }
  }, [status]);

  useEffect(() => { if (user?.role === 'super_admin') fetchAll(); }, [fetchAll, user]);

  const analyzeOne = async (id) => {
    try {
      await axios.post(`${API}/api/ui-bugs/${id}/analyze`);
      toast.success('Analyzed');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    }
  };

  const runNightly = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/api/ui-bugs/run-nightly`, null, { params: { limit: 20 } });
      toast.success(`Analyzed ${r.data.analyzed} · Failed ${r.data.failed}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch failed');
    } finally { setRunning(false); }
  };

  const setStatusOne = async (id, next) => {
    try {
      await axios.post(`${API}/api/ui-bugs/${id}/status`, { status: next });
      fetchAll();
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this report permanently?')) return;
    try {
      await axios.delete(`${API}/api/ui-bugs/${id}`);
      setReports((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const openScreenshot = async (id) => {
    try {
      const r = await axios.get(`${API}/api/ui-bugs/${id}`, { params: { include_image: true } });
      setScreenshotOpen({
        url: `data:${r.data.media_type};base64,${r.data.image_base64}`,
        route: (() => { try { return new URL(r.data.url).pathname; } catch { return r.data.url; } })(),
      });
    } catch (e) {
      toast.error('Could not load screenshot');
    }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!user || user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-6xl mx-auto page-shell" data-testid="ui-bug-queue-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bug className="h-7 w-7 text-amber-500" /> UI Bug Queue<PageInfoTooltip pageKey="ui-bug-queue" />
          </h1>
          <p className="text-gray-500 mt-1">
            User-filed screenshots. Batch-analyze with Kaharian vision (Claude → Gemini → Haiku) then paste suggestions into VibeKonnect.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={busy}
            data-testid="ui-bug-refresh"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1">Refresh</span>
          </Button>
          <Button
            size="sm"
            onClick={runNightly}
            disabled={running || stats.pending === 0}
            className="bg-cyan-600 hover:bg-cyan-500"
            data-testid="ui-bug-run-batch"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
            Analyze batch (up to 20)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[
          { k: 'pending', label: 'Pending', color: 'text-amber-700' },
          { k: 'analyzed', label: 'Analyzed', color: 'text-cyan-700' },
          { k: 'fixed', label: 'Fixed', color: 'text-emerald-700' },
          { k: 'dismissed', label: 'Dismissed', color: 'text-zinc-600' },
          { k: 'new_last_24h', label: 'Last 24h', color: 'text-gray-800' },
        ].map((s) => (
          <div key={s.k} className="border border-gray-200 rounded p-2 bg-white" data-testid={`ui-bug-stat-${s.k}`}>
            <div className="text-[10px] uppercase text-gray-500 tracking-wide">{s.label}</div>
            <div className={`text-xl font-semibold ${s.color}`}>{stats[s.k] || 0}</div>
          </div>
        ))}
      </div>

      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="pending" data-testid="ui-bug-tab-pending" className="text-xs">Pending</TabsTrigger>
          <TabsTrigger value="analyzed" data-testid="ui-bug-tab-analyzed" className="text-xs">Analyzed</TabsTrigger>
          <TabsTrigger value="fixed" data-testid="ui-bug-tab-fixed" className="text-xs">Fixed</TabsTrigger>
          <TabsTrigger value="dismissed" data-testid="ui-bug-tab-dismissed" className="text-xs">Dismissed</TabsTrigger>
          <TabsTrigger value="all" data-testid="ui-bug-tab-all" className="text-xs">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {busy && reports.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-500 bg-white border border-gray-200 rounded" data-testid="ui-bug-empty">
          <Bug className="h-6 w-6 text-gray-300 mx-auto mb-2" />
          No {status === 'all' ? '' : status} reports.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ui-bug-list">
          {reports.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              onAnalyze={analyzeOne}
              onStatus={setStatusOne}
              onDelete={del}
              onOpen={openScreenshot}
            />
          ))}
        </div>
      )}

      <Dialog open={!!screenshotOpen} onOpenChange={(o) => { if (!o) setScreenshotOpen(null); }}>
        <DialogContent className="max-w-5xl" data-testid="ui-bug-screenshot-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-mono text-gray-600">{screenshotOpen?.route}</DialogTitle>
          </DialogHeader>
          {screenshotOpen && (
            <img src={screenshotOpen.url} alt="bug screenshot" className="w-full h-auto max-h-[75vh] object-contain rounded border border-gray-200" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
